/**
 * Admin — activités d'un compte (fiche de `/tutor/admin/users`, migration 0504).
 * RPC `admin_get_user_activity` (admin seul) : exercices et Warmup (commencés
 * ou terminés), automatismes, diagnostics, tests de pack, lives, notes d'éval,
 * achats de SKILLCARDS — du plus récent au plus ancien, page par page.
 * RPC postérieure aux types générés → client générique.
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { formatEuros } from '@jeraisonne/db/cards';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export const ADMIN_ACTIVITY_PAGE_SIZE = 20;

export type AdminActivityKind =
  | 'exercise'
  | 'warmup'
  | 'automatismes'
  | 'diagnostic'
  | 'pack_test'
  | 'live'
  | 'evaluation'
  | 'purchase';

export interface AdminActivityItem {
  key: string;
  kind: AdminActivityKind | string;
  ref_id: string;
  target_id: string | null;
  title: string;
  status: string | null;
  answered: number | null;
  correct: number | null;
  total: number | null;
  score: number | null;
  max_score: number | null;
  started_at: string | null;
  ended_at: string | null;
  at: string;
}

export interface AdminActivityPage {
  items: AdminActivityItem[];
  has_more: boolean;
  next_before: string | null;
}

export function useAdminUserActivity(userId: string | null) {
  return useInfiniteQuery({
    queryKey: ['admin-user-activity', userId],
    enabled: !!userId,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }): Promise<AdminActivityPage> => {
      const { data, error } = await db.rpc('admin_get_user_activity', {
        p_user_id: userId,
        p_limit: ADMIN_ACTIVITY_PAGE_SIZE,
        p_before: pageParam,
      });
      if (error) throw new Error(error.message);
      const page = (data ?? {}) as Partial<AdminActivityPage>;
      return {
        items: page.items ?? [],
        has_more: !!page.has_more,
        next_before: page.next_before ?? null,
      };
    },
    getNextPageParam: (last) => (last.has_more ? last.next_before : undefined),
  });
}

const KIND_LABELS: Record<AdminActivityKind, string> = {
  exercise: 'Exercice',
  warmup: 'Warmup',
  automatismes: 'Automatismes',
  diagnostic: 'Diagnostic',
  pack_test: 'Test de pack',
  live: 'Live School',
  evaluation: 'Note d’éval',
  purchase: 'Achat',
};

export function activityKindLabel(kind: string): string {
  return KIND_LABELS[kind as AdminActivityKind] ?? 'Activité';
}

export type ActivityStatusTone = 'progress' | 'done' | 'muted';

/** Statut lisible : « En cours », « Terminé », « Abandonné », « Payé ». */
export function activityStatus(
  item: Pick<AdminActivityItem, 'kind' | 'status'>,
): { label: string; tone: ActivityStatusTone } | null {
  if (item.kind === 'purchase') return { label: 'Payé', tone: 'done' };
  if (item.kind === 'evaluation') return null;
  switch (item.status) {
    case 'in_progress':
    case 'pending':
      return { label: 'En cours', tone: 'progress' };
    case 'completed':
      return { label: 'Terminé', tone: 'done' };
    case 'abandoned':
      return { label: 'Abandonné', tone: 'muted' };
    default:
      return null;
  }
}

const plural = (n: number) => (n > 1 ? 's' : '');
const formatNumber = (n: number) =>
  Number.isInteger(n) ? String(n) : n.toFixed(1).replace('.', ',');

/** Ligne de résumé chiffré d'une activité (réponses, justes, score). Pure. */
export function activitySummary(item: AdminActivityItem): string {
  const parts: string[] = [];
  if (item.kind === 'purchase') {
    if (item.score !== null) parts.push(formatEuros(item.score));
    if (item.total) parts.push(`${item.total} carte${plural(item.total)}`);
    return parts.join(' · ');
  }
  if (item.kind === 'evaluation') {
    if (item.score !== null) {
      parts.push(`${formatNumber(item.score)} / ${formatNumber(item.max_score ?? 20)}`);
    }
    return parts.join(' · ');
  }
  const answered = item.answered ?? 0;
  if (item.total) {
    parts.push(`${answered}/${item.total} répondue${plural(item.total)}`);
  } else if (answered > 0) {
    parts.push(`${answered} réponse${plural(answered)}`);
  }
  if (item.correct !== null && answered > 0) {
    parts.push(`${item.correct} juste${plural(item.correct)}`);
  }
  if (item.score !== null) {
    if (item.kind === 'live') {
      parts.push(`${formatNumber(item.score)} pts`);
    } else if (item.status === 'completed') {
      parts.push(`${Math.round(item.score)} %`);
    }
  }
  return parts.join(' · ');
}

/** Où ouvrir l'activité depuis la fiche (résultats d'un essai, rapport). Pure. */
export function activityLink(item: Pick<AdminActivityItem, 'kind' | 'ref_id' | 'status'>) {
  if (item.kind === 'exercise') {
    return { to: '/attempts/$attemptId', params: { attemptId: item.ref_id } } as const;
  }
  if (item.kind === 'diagnostic' && item.status === 'completed') {
    return {
      to: '/tutor/diagnostic/$diagnosticId',
      params: { diagnosticId: item.ref_id },
    } as const;
  }
  return null;
}
